import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { hexToHsl } from '../utils/hex-to-hsl';

export type Theme = 'light' | 'dark' | 'system';

export interface ThemeColors {
  primary?: string;
  background?: string;
  accent?: string;
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  /**
   * Optional hex colors. When provided, ThemeProvider converts each to HSL
   * and injects them as inline CSS variables on `document.documentElement`,
   * overriding the defaults from `tokens.css`. Tailwind tokens consume these
   * via `hsl(var(--primary))` etc.
   */
  colors?: ThemeColors;
}

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function applyTheme(theme: Theme): 'light' | 'dark' {
  const root = window.document.documentElement;
  const resolved =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);
  return resolved;
}

/**
 * Publish the portal's brand colors as CSS variables on the root element.
 *
 * Inline styles on `<html>` outrank the `:root` rules in tokens.css, so these
 * are authoritative for the whole document — including Radix surfaces that
 * render into `document.body`.
 *
 * Each color is also written as a `--brand-*` alias. Custom properties are
 * inherited, so any *descendant* that re-declares a token would beat the root
 * value even though it was set inline; such a scope must consume
 * `var(--brand-primary, <default>)` rather than redeclaring `--primary`. Do not
 * introduce a scope like that to add a second look — that is precisely the
 * parallel design system this package replaced.
 */
function applyColors(colors: ThemeColors | undefined, resolvedTheme: 'light' | 'dark'): void {
  if (typeof window === 'undefined') return;
  const root = window.document.documentElement;
  const apply = (varName: string, brandVarName: string, hex: string | undefined): void => {
    if (!hex) return;
    const hsl = hexToHsl(hex);
    if (!hsl) return;
    root.style.setProperty(varName, hsl);
    root.style.setProperty(brandVarName, hsl);
  };
  apply('--primary', '--brand-primary', colors?.primary);
  /*
   * The brand canvas is a light hex, and these inline styles outrank `.dark`.
   * Publishing it in dark mode therefore repaints the page light while
   * `--foreground` stays the dark theme's near-white — every control drawn
   * directly on the canvas (filter bars, toolbars, bare text) becomes
   * light-on-light. Dark mode keeps the dark canvas from tokens.css; the brand
   * still shows through `--primary`, `--accent`, and `--ring`.
   *
   * `--brand-background` is still published either way: it is an alias for
   * anything that wants the brand's own colour, not the page canvas.
   */
  if (resolvedTheme === 'dark') {
    root.style.removeProperty('--background');
    const brandBackground = colors?.background ? hexToHsl(colors.background) : null;
    if (brandBackground) root.style.setProperty('--brand-background', brandBackground);
  } else {
    apply('--background', '--brand-background', colors?.background);
  }
  apply('--accent', '--brand-accent', colors?.accent);
  /*
   * The focus ring is the brand colour, not a token of its own. Without this it
   * keeps the default from tokens.css, so a blue portal draws burgundy focus
   * rings on every input, button, and auto-focused dialog control.
   */
  apply('--ring', '--brand-ring', colors?.primary);
}

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'app-theme',
  colors,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return defaultTheme;
    return (window.localStorage.getItem(storageKey) as Theme | null) ?? defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => applyTheme(theme));

  useEffect(() => {
    setResolvedTheme(applyTheme(theme));
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, theme);
    }
  }, [theme, storageKey]);

  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolvedTheme(applyTheme('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  // `resolvedTheme` is a dependency because the canvas is theme-dependent:
  // switching to dark has to withdraw the inline light background, not just
  // add a class that the inline style outranks.
  useEffect(() => {
    applyColors(colors, resolvedTheme);
  }, [colors?.primary, colors?.background, colors?.accent, resolvedTheme]);

  const value: ThemeContextValue = {
    theme,
    resolvedTheme,
    setTheme: setThemeState,
    toggle: () => setThemeState(resolvedTheme === 'dark' ? 'light' : 'dark'),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
